<template>
  <v-container v-if="list"
               fluid>
    <PageTitle icon="fal fa-fw fa-users">
      Edit Email List
      <template v-slot:buttons>
        <v-btn v-if="isEmailComposerEnabled"
               :to="getSendEmailRoute()"
               color="primary"
               class="mr-4">
          Email this list
        </v-btn>

        <v-btn v-else
               :to="getSendCampaignRoute()"
               color="primary"
               class="mr-4">
          Send Campaign
        </v-btn>

        <v-btn @click.prevent="showCopyListDialog = true">
          <v-icon left>
            fal fa-fw fa-copy
          </v-icon>
          Copy List
        </v-btn>
      </template>
    </PageTitle>

    <copy-list-dialog v-if="showCopyListDialog"
                      :list="list"
                      :dialog="showCopyListDialog"
                      @close="showCopyListDialog = false">
    </copy-list-dialog>

    <v-form @submit.prevent="submit"
            v-if="list && !loading"
            :disabled="form.busy">
      <v-card class="mb-4">
        <v-card-text>
          <v-text-field v-model="form.name"
                        label="List name"
                        outlined
                        :rules="[rules.required]"
                        :error-messages="form.errors.get('name')">
          </v-text-field>

          <v-text-field v-model="form.description"
                        label="Description"
                        outlined
                        maxlength="500"
                        counter="500"
                        :error-messages="form.errors.get('description')">
          </v-text-field>
        </v-card-text>
        <v-card-actions class="px-4">
          <v-btn type="submit"
                 color="primary"
                 :disabled="form.busy">
            Save
          </v-btn>
          <v-btn :to="{name: `${$root.routePrefix}.mailcoach.lists`}">
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn v-if="canBeDeleted"
                 color="error"
                 :disabled="form.busy"
                 @click.prevent="deleteList">
            Delete List
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>

    <v-form @submit.prevent=""
            ref="addContactForm">
      <v-card class="mb-4">
        <v-card-title>
          <div>
            Add Contacts to this list
          </div>
          <v-spacer></v-spacer>
          <sync-user-contacts-button @updated="fetchData(listID)">
          </sync-user-contacts-button>
        </v-card-title>
        <v-card-text>
          <search-for-user-contact @selected="addContact">
          </search-for-user-contact>
        </v-card-text>
      </v-card>
    </v-form>

    <v-card class="mb-4">
      <subscribers-table :list="list"
                         :loading="loading"
                         :search="search"
                         :subscribers="subscribers"
                         @updated="fetchData(list.id)">
      </subscribers-table>
    </v-card>

    <v-dialog v-model="showContactsDialog"
              max-width="800px"
              persistent>
      <v-card flat
              tile>
        <v-toolbar color="primary"
                   dark
                   flat
                   tile>
          <v-toolbar-title>
            Add Contacts
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn @click.prevent="showContactsDialog = false"
                 icon>
            <v-icon>
              fal fa-times
            </v-icon>
          </v-btn>
        </v-toolbar>

      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
import {mapGetters} from 'vuex';
import {Form} from 'vform';
import {isObject, orderBy} from 'lodash';
import formValidation from '../../plugins/formValidation';
import mailcoachService from '../../services/mailcoachService';
import SubscribersTable from './wizard/components/SubscribersTable';
import SearchForUserContact from "./components/SearchForUserContact";
import CopyListDialog from "./components/CopyListDialog";
import SyncUserContactsButton from "../user-contacts/components/SyncUserContactsButton";
import DOMPurify from 'dompurify';
import PageTitle from '../../components/PageTitle.vue';

export default {
  components: {PageTitle, SyncUserContactsButton, CopyListDialog, SearchForUserContact, SubscribersTable},
  mixins: [formValidation],
  data() {
    return {
      campaigns: [],
      form: new Form({
        name: '',
        description: null, // MLD-5213
      }),
      list: null,
      loading: false,
      search: null,
      showContactsDialog: false,
      showCopyListDialog: false, // MLD-3318
      subscribers: [],
    }
  },
  computed: {
    ...mapGetters([
      'isAdmin',
      'isEmailComposerEnabled', // MLD-6403
      'isMember',
      'team',
      'user',
    ]),
    canBeDeleted() {
      return true;
      // You can only delete the list if it hasn't been used in a campaign
      // return !this.loading && this.campaigns.length === 0;
    },
    listID() {
      return parseInt(this.$route.params.listID);
    },
  },
  beforeRouteUpdate(to, from, next) {
    console.log('beforeRouteUpdate', to);
    if (to.params.listID) {
      this.fetchData(to.params.listID)
    }
    next();
  },
  created() {
    this.fetchData(this.listID);
  },
  methods: {
    fetchData(listID) {
      this.loading = true;
      return Promise.all([
        this.getList(listID),
        this.getSubscribers(listID),
        this.getCampaigns(listID),
      ])
        .finally(() => {
          this.loading = false;
        })
    },
    getList(listID) {
      return mailcoachService.getList(listID)
        .then(data => {
          this.list = data;
          this.form.name = data.name;
          this.form.description = data.description; // MLD-5213
        })
    },
    getCampaigns(listID) {
      this.campaigns = [];
      return mailcoachService.getCampaigns({email_list_id: listID})
        .then(data => {
          this.campaigns = data;
        })
    },
    deleteList() {
      if (!confirm('Are you sure you want to delete this list?')) {
        return;
      }
      return axios.delete(`/api/mailcoach/lists/${this.list.id}`)
        .then(() => {
          this.$router.replace({name: `${this.$root.routePrefix}.mailcoach.lists`});
        })
    },
    async getSubscribers(listID) {
      this.loading = true;
      this.subscribers = [];

      try {
        // Use the optimized getAllSubscribers method in batches.
        const allSubscribers = await mailcoachService.getAllSubscribers(listID);

        // Log the total number of subscribers for verification
        //console.log(`Total subscribers fetched: ${allSubscribers.length}`);

        // Sort the results by email address
        this.subscribers = orderBy(allSubscribers, ['contact.email'], ['asc']);
      } catch (error) {
        console.error('Error fetching subscribers:', error);
        // You may want to add error handling UI here
      } finally {
        this.loading = false;
      }
    },
    submit() {
      this.form.put(`/api/mailcoach/lists/${this.list.id}`)
        .then(response => {
          this.$router.push({name: `${this.$root.routePrefix}.mailcoach.lists`});
        })
    },
    addContact(contact) {
      if (isObject(contact) && contact.id) {
        mailcoachService.addContacts(this.list.id, [contact.id])
          .then(data => {
            this.$refs.addContactForm.reset();
            this.$toast.success(`Added ${DOMPurify.sanitize(contact.email)} to this list.`);
            this.getSubscribers(this.list.id);
          })
      }
    },
    getSendEmailRoute() { // MLD-6403
      return {
        name: `${this.$root.routePrefix}.send.email`,
        query: {
          list_id: this.list.id,
        },
      }
    },
    getSendCampaignRoute() {
      return {
        name: `${this.$root.routePrefix}.mailcoach.campaign.wizard`,
        query: {
          list_id: this.list.id,
        },
      }
    }
  }
}
</script>
